import { authClient } from "@features/auth/authClient";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { commitMutation, fetchQuery, graphql } from "relay-runtime";
import { toast } from "sonner";
import type { raffleIndexEnterMutation } from "./__generated__/raffleIndexEnterMutation.graphql";
import type { raffleIndexQuery } from "./__generated__/raffleIndexQuery.graphql";
import type { raffleIndexRafflesQuery } from "./__generated__/raffleIndexRafflesQuery.graphql";

const query = graphql`
	query raffleIndexQuery($hubMetaName: String!) {
		hubMeta(by: { name: $hubMetaName }) @required(action: THROW) {
			id
			name
			hub @required(action: THROW) {
				id
				raffle {
					ready
					reservations {
						id
						active
						usecase
					}
				}
			}
		}
	}
`;

const rafflesQuery = graphql`
	query raffleIndexRafflesQuery($userId: UUID!, $hubId: UUID!) {
		activeRaffles: raffles(filter: { hubId: $hubId }, pagination: { page: 0, pageSize: 1000000 }) {
			nodes {
				id
				status
				user {
					id
				}
			}
		}
		userRaffles: raffles(filter: { userId: $userId, hubId: $hubId }, pagination: { page: 0, pageSize: 1000000 }) {
			nodes {
				id
				status
				hub {
					id
				}
			}
		}
	}
`;

const raffleEnterMutationGraphql = graphql`
	mutation raffleIndexEnterMutation($hubId: UUID!) {
		raffleEnter(input: { hubId: $hubId }) {
			id
			status
			hub {
				id
			}
			user {
				id
			}
		}
	}
`;

let redirectCount = 0;

export const Route = createFileRoute("/$hubMetaName/raffle/")({
	beforeLoad: () => {
		document.title = "bacehub | Startseite";
	},
	component: RouteComponent,
	loader: async (ctx) => {
		const { hubMetaName } = ctx.params;

		const session = await authClient.getSession();
		const userId = session.data?.user?.id;

		if (!userId) {
			redirectCount++;
			if (redirectCount > 10) {
				toast.error("Detected redirect loop, we are redirecting you to the home page now");
				throw redirect({
					to: "/",
				});
			}
			throw redirect({
				to: "/auth",
				search: {
					show: "sign-in",
					redirectToPath: location.pathname,
				},
			});
		}
		redirectCount = 0;

		let hubData: raffleIndexQuery["response"] | undefined;
		try {
			hubData = await fetchQuery<raffleIndexQuery>(ctx.context.relayEnvironment, query, {
				hubMetaName,
			}).toPromise();
		} catch {
			toast.error("Failed to load hub data. Please try again.");
			throw redirect({
				to: "/$hubMetaName",
				params: { hubMetaName },
			});
		}

		if (!hubData) {
			throw redirect({
				to: "/$hubMetaName",
				params: { hubMetaName },
			});
		}

		const hub = hubData.hubMeta.hub;

		let rafflesData: raffleIndexRafflesQuery["response"] | undefined;
		try {
			rafflesData = await fetchQuery<raffleIndexRafflesQuery>(ctx.context.relayEnvironment, rafflesQuery, {
				userId,
				hubId: hub.id,
			}).toPromise();
		} catch {
			toast.error("Failed to load raffle data. Please try again.");
			throw redirect({
				to: "/$hubMetaName",
				params: { hubMetaName },
			});
		}

		const activeRaffles = rafflesData?.activeRaffles?.nodes || [];
		const userRaffles = rafflesData?.userRaffles?.nodes || [];

		const activeRaffle = activeRaffles[0];

		if (activeRaffle && activeRaffle.status === "ONGOING" && activeRaffle.user.id !== userId) {
			throw redirect({
				to: "/$hubMetaName/raffle/another-one-playing",
				params: { hubMetaName },
			});
		}

		if (userRaffles && userRaffles[0]?.status === "ONGOING") {
			throw redirect({
				to: "/$hubMetaName/raffle/$raffleId/play",
				params: { hubMetaName, raffleId: userRaffles[0].id },
			});
		}

		if (userRaffles && (userRaffles[0]?.status === "LOSER" || userRaffles[0]?.status === "WINNER")) {
			throw redirect({
				to: "/$hubMetaName/raffle/no-more-playing",
				params: { hubMetaName },
			});
		}

		const hubRaffle = hub.raffle;
		const activePrizeReservationsCount =
			hubRaffle?.reservations.filter((reservation) => reservation.active && reservation.usecase === "PRIZE").length ?? 0;

		if (!hubRaffle || hubRaffle.ready !== true || activePrizeReservationsCount < 1) {
			throw redirect({
				to: "/$hubMetaName/raffle/another-one-playing",
				params: { hubMetaName },
			});
		}

		let raffleEnterResult: raffleIndexEnterMutation["response"] | undefined;
		try {
			raffleEnterResult = await new Promise<raffleIndexEnterMutation["response"]>((resolve, reject) => {
				commitMutation<raffleIndexEnterMutation>(ctx.context.relayEnvironment, {
					mutation: raffleEnterMutationGraphql,
					variables: {
						hubId: hub.id,
					},
					onCompleted: (response) => {
						resolve(response);
					},
					onError: (error) => {
						reject(error);
					},
				});
			});
		} catch (error: any) {
			const errorCode = error?.errors?.[0]?.extensions?.code ?? error?.extensions?.code;
			const errorDetails = error?.errors?.[0]?.extensions?.details;

			if (errorCode === "OTHER_RAFFLE_IN_PROGRESS") {
				throw redirect({
					to: "/$hubMetaName/raffle/another-one-playing",
					params: { hubMetaName },
				});
			}

			if (errorCode === "PRECONDITION_FAILED" && errorDetails === "No prize in Hub") {
				throw redirect({
					to: "/$hubMetaName/raffle/another-one-playing",
					params: { hubMetaName },
				});
			}

			// Fallback: any unhandled error should redirect instead of leaving the user stuck
			console.error("[raffle/index] Unhandled error during raffleEnter:", error);
			toast.error("Something went wrong. Please try again.");
			throw redirect({
				to: "/$hubMetaName/raffle/another-one-playing",
				params: { hubMetaName },
			});
		}

		if (raffleEnterResult?.raffleEnter) {
			throw redirect({
				to: "/$hubMetaName/raffle/$raffleId/play",
				params: {
					hubMetaName,
					raffleId: raffleEnterResult.raffleEnter.id,
				},
			});
		}

		// Mutation succeeded but returned no raffle data — treat as unavailable
		toast.error("Raffle is currently not available. Please try again.");
		throw redirect({
			to: "/$hubMetaName/raffle/another-one-playing",
			params: { hubMetaName },
		});
	},
});

function RouteComponent() {
	return <div>Loading raffle...</div>;
}
